import React from 'react';

// Using simple SVG icons instead of lucide-react to avoid dependency issues
const PlusIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
  </svg>
);

const CheckIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const MinusIcon: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
  </svg>
);

export interface SimpleTagChipProps {
  tag: string;
  mode: 'inclusive' | 'required' | 'excluded';
  onClick: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  category?: string;
  disabled?: boolean;
}

/**
 * A visual tag chip that shows different logic modes with clear color and icon coding
 * - Inclusive (OR): Blue background with "+" icon 
 * - Required (AND): Green background with "✓" icon
 * - Excluded (NOT): Red background with "-" icon
 */
export const SimpleTagChip: React.FC<SimpleTagChipProps> = ({
  tag,
  mode,
  onClick,
  className = '',
  size = 'md',
  category,
  disabled = false
}) => {
  // Visual styling based on mode
  const modeStyles = {
    inclusive: {
      bg: 'bg-blue-100 hover:bg-blue-200',
      text: 'text-blue-800',
      border: 'border-blue-300',
      ring: 'focus:ring-blue-500',
      darkBg: 'dark:bg-blue-900',
      darkText: 'dark:text-blue-200',
      darkBorder: 'dark:border-blue-700'
    },
    required: {
      bg: 'bg-green-100 hover:bg-green-200',
      text: 'text-green-800',
      border: 'border-green-300',
      ring: 'focus:ring-green-500',
      darkBg: 'dark:bg-green-900',
      darkText: 'dark:text-green-200',
      darkBorder: 'dark:border-green-700'
    },
    excluded: {
      bg: 'bg-red-100 hover:bg-red-200',
      text: 'text-red-800',
      border: 'border-red-300',
      ring: 'focus:ring-red-500',
      darkBg: 'dark:bg-red-900',
      darkText: 'dark:text-red-200',
      darkBorder: 'dark:border-red-700'
    }
  };

  // Icons for each mode
  const modeIcons = {
    inclusive: PlusIcon,
    required: CheckIcon,
    excluded: MinusIcon
  };

  // Size variants
  const sizeStyles = {
    sm: {
      container: 'px-2 py-0.5 text-xs',
      icon: 'h-2.5 w-2.5 mr-1'
    },
    md: {
      container: 'px-2.5 py-0.5 text-xs',
      icon: 'h-3 w-3 mr-1'
    },
    lg: {
      container: 'px-3 py-1 text-sm',
      icon: 'h-3.5 w-3.5 mr-1.5'
    }
  };

  const IconComponent = modeIcons[mode];
  const styles = modeStyles[mode];
  const sizes = sizeStyles[size];

  // Accessibility labels
  const modeLabels = {
    inclusive: 'Include any (OR)',
    required: 'Must have (AND)', 
    excluded: 'Exclude (NOT)'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center rounded-full font-medium border transition-all duration-200
        ${styles.bg} ${styles.text} ${styles.border}
        ${styles.darkBg} ${styles.darkText} ${styles.darkBorder}
        ${sizes.container}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-1 ${styles.ring}
        ${className}
      `}
      title={`${tag} - ${modeLabels[mode]}${category ? ` (${category})` : ''}`}
      aria-label={`Filter by ${tag} using ${modeLabels[mode]} logic${category ? ` in ${category} category` : ''}`}
    >
      <IconComponent className={sizes.icon} />
      <span className="truncate max-w-24 sm:max-w-32 md:max-w-none">
        {tag}
      </span>
    </button>
  );
};

// Helper component for displaying the mode legend/help
export const TagChipLegend: React.FC<{ className?: string }> = ({ className = '' }) => {
  const examples = [
    { mode: 'inclusive' as const, label: 'OR', description: 'Include any' },
    { mode: 'required' as const, label: 'AND', description: 'Must have' },
    { mode: 'excluded' as const, label: 'NOT', description: 'Exclude' }
  ];

  return (
    <div className={`flex flex-wrap items-center gap-2 text-xs text-gray-600 dark:text-gray-400 ${className}`}>
      <span className="font-medium">Tag modes:</span>
      {examples.map(({ mode, label, description }) => (
        <div key={mode} className="flex items-center gap-1">
          <SimpleTagChip
            tag={label}
            mode={mode}
            onClick={() => {}} // No-op for legend
            size="sm"
            disabled
          />
          <span>{description}</span>
        </div>
      ))}
    </div>
  );
};

// Utility component for grouping tags by category
export interface TagGroupProps {
  title?: string;
  tags: Array<{
    id: string;
    value: string;
    mode: 'inclusive' | 'required' | 'excluded';
    category?: string;
  }>;
  onTagClick: (tagId: string, value: string, category?: string) => void;
  className?: string;
}

export const SimpleTagGroup: React.FC<TagGroupProps> = ({
  title,
  tags,
  onTagClick,
  className = ''
}) => {
  if (tags.length === 0) return null;

  return (
    <div className={className}>
      {title && (
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {title}
        </h4>
      )}
      <div className="flex flex-wrap gap-1.5">
        {tags.map(({ id, value, mode, category }) => (
          <SimpleTagChip
            key={id}
            tag={value}
            mode={mode}
            category={category}
            onClick={() => onTagClick(id, value, category)}
          />
        ))}
      </div>
    </div>
  );
}; 